import { stripAccountScopePath } from './businessNav.js';

export type BusinessCrumb = {
  href?: string;
  label: string;
};

type CrumbRule = {
  test: (pathname: string) => boolean;
  trail: BusinessCrumb[];
};

function exactOrPrefix(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(`${href}/`);
}

/**
 * 07-03: breadcrumbs from the third level down (`People › Departments › Legal`).
 * Longer / more specific rules first.
 */
const CRUMB_RULES: readonly CrumbRule[] = [
  {
    test: (p) => exactOrPrefix(p, '/department-editor'),
    trail: [
      { label: 'People', href: '/employee-list' },
      { label: 'Departments', href: '/department-list' },
      { label: 'Department' },
    ],
  },
  {
    test: (p) => exactOrPrefix(p, '/employee-detail'),
    trail: [
      { label: 'People', href: '/employee-list' },
      { label: 'Employees', href: '/employee-list' },
      { label: 'Employee' },
    ],
  },
  {
    test: (p) => exactOrPrefix(p, '/invite-employees'),
    trail: [
      { label: 'People', href: '/employee-list' },
      { label: 'Employees', href: '/employee-list' },
      { label: 'Invite' },
    ],
  },
  {
    test: (p) => exactOrPrefix(p, '/bulk-employee-import'),
    trail: [
      { label: 'People', href: '/employee-list' },
      { label: 'Employees', href: '/employee-list' },
      { label: 'Bulk import' },
    ],
  },
  {
    test: (p) => exactOrPrefix(p, '/hierarchy-view'),
    trail: [
      { label: 'People', href: '/employee-list' },
      { label: 'Departments', href: '/department-list' },
      { label: 'Hierarchy' },
    ],
  },
  {
    test: (p) => /^\/budget-monitor\/[^/]+/.test(p),
    trail: [
      { label: 'Budgets', href: '/budget-monitor' },
      { label: 'Monitor', href: '/budget-monitor' },
      { label: 'Budget' },
    ],
  },
  {
    test: (p) => exactOrPrefix(p, '/policy-simulator'),
    trail: [
      { label: 'Policies', href: '/policy-list' },
      { label: 'Editor', href: '/policy-editor' },
      { label: 'Simulator' },
    ],
  },
  {
    test: (p) => exactOrPrefix(p, '/policy-editor'),
    trail: [
      { label: 'Policies', href: '/policy-list' },
      { label: 'List', href: '/policy-list' },
      { label: 'Editor' },
    ],
  },
  {
    test: (p) => exactOrPrefix(p, '/approval-detail') || exactOrPrefix(p, '/approvals'),
    trail: [
      { label: 'Approvals', href: '/approval-queue' },
      { label: 'Queue', href: '/approval-queue' },
      { label: 'Request' },
    ],
  },
  {
    test: (p) => /\/invoices\/[^/]+\/query\/?$/.test(p) || exactOrPrefix(p, '/invoice-query'),
    trail: [
      { label: 'Invoices', href: '/invoice-list' },
      { label: 'Invoice', href: '/invoice-list' },
      { label: 'Query' },
    ],
  },
  {
    test: (p) => /\/invoices\/[^/]+\/payment\/?$/.test(p) || exactOrPrefix(p, '/payment-record'),
    trail: [
      { label: 'Invoices', href: '/invoice-list' },
      { label: 'Invoice', href: '/invoice-list' },
      { label: 'Payment' },
    ],
  },
  {
    test: (p) => exactOrPrefix(p, '/invoice-detail') || exactOrPrefix(p, '/invoices'),
    trail: [
      { label: 'Invoices', href: '/invoice-list' },
      { label: 'List', href: '/invoice-list' },
      { label: 'Invoice' },
    ],
  },
  {
    test: (p) => exactOrPrefix(p, '/delegation-settings'),
    trail: [
      { label: 'Settings', href: '/account-settings' },
      { label: 'Approval chain', href: '/approval-chain' },
      { label: 'Delegation' },
    ],
  },
];

export function buildBusinessCrumbs(
  pathname: string,
  options?: { lastLabel?: string | null; breadcrumbs?: BusinessCrumb[] | null },
): BusinessCrumb[] {
  if (options?.breadcrumbs?.length) {
    const trail = options.breadcrumbs.map((crumb) => ({ ...crumb }));
    if (options.lastLabel?.trim() && trail.length > 0) {
      trail[trail.length - 1] = { ...trail[trail.length - 1]!, label: options.lastLabel.trim() };
    }
    return trail;
  }

  const path = stripAccountScopePath(pathname);
  const rule = CRUMB_RULES.find((entry) => entry.test(path));
  if (!rule) return [];
  const trail = rule.trail.map((crumb) => ({ ...crumb }));
  if (options?.lastLabel?.trim() && trail.length > 0) {
    trail[trail.length - 1] = { ...trail[trail.length - 1]!, label: options.lastLabel.trim() };
  }
  return trail;
}

/** 07-03 — render from the third level down. */
export function shouldShowBusinessCrumbs(trail: readonly BusinessCrumb[]): boolean {
  return trail.length >= 3;
}
